import { useContext, useMemo, useRef, useState, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings,
  Info,
  ChevronUp,
  ChevronDown,
  FileText,
  FileSpreadsheet,
  Download,
  AlertCircle,
  Trash2,
  Calendar,
  Loader2,
  Eye,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  RelatoriosContext,
  type ItemRelatorio,
  type Relatorio,
} from '@/contexts/RelatoriosContext';
import { useToast } from '@/hooks/use-toast';
import { exportToExcel, formatExcelFileName } from '@/lib/exportToExcel';
import { cn } from '@/lib/utils';
import { formatCNPJ } from '@/lib/utils/formatters';
import { useGerarLinkTemporario } from '@/services/General';
import {
  listarAgendamentosApi,
  listarCamposApi,
  listarDadosApi,
  listarItensExcluidosApi,
  useAguardarSolicitacao,
  useDeletarAgendamento,
  useGerarRelatorioCSV,
  useGerarRelatorioPDF,
  type AgendamentoApi,
  type DadosRelatorioItem,
} from '@/services/Relatorios';
import { ModalAgendarProcessamento } from './ModalAgendarProcessamento';
import { ModalConfiguracao } from './ModalConfiguracao';
import { ModalDescricaoRegra } from './ModalDescricaoRegra';
import { ModalVisualizarDados } from './ModalVisualizarDados';

interface TabelaRelatoriosProps {
  agendamentosSelecionados: Set<string>;
  setAgendamentosSelecionados: (value: Set<string>) => void;
}

type OrdemNumeroRelatorio = 'asc' | 'desc';

interface ActionTooltipButtonProps {
  label: string;
  children: React.ReactNode;
}

function ActionTooltipButton({ label, children }: ActionTooltipButtonProps) {
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setPosition({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  };

  const showTooltip = () => {
    updatePosition();
    setVisible(true);
  };

  const hideTooltip = () => setVisible(false);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        title={label}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            className="pointer-events-none fixed z-2147483647 -translate-x-1/2 -translate-y-full rounded-md border border-border bg-foreground px-3 py-1.5 text-xs text-background shadow-md"
            style={{ top: position.top, left: position.left }}
          >
            {label}
          </div>,
          document.body
        )}
    </>
  );
}

export function TabelaRelatorios({
  agendamentosSelecionados,
  setAgendamentosSelecionados,
}: TabelaRelatoriosProps) {
  const ctx = useContext(RelatoriosContext);
  if (!ctx) {
    throw new Error('TabelaRelatorios deve ser usado dentro de <RelatoriosProvider>');
  }

  const {
    relatorios,
    paginaAtual,
    itensPorPagina,
    hasEmpresaSelecionada,
    isLoading,
    cnpjsSelecionados,
  } = ctx;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expandido, setExpandido] = useState<string | null>(null);
  const [ordemNumeroRelatorio, setOrdemNumeroRelatorio] = useState<OrdemNumeroRelatorio>('asc');
  const [modalConfiguracaoAberto, setModalConfiguracaoAberto] = useState(false);
  const [relatorioSelecionado, setRelatorioSelecionado] = useState<string | null>(null);
  const [relatorioConfigurar, setRelatorioConfigurar] = useState<Relatorio | null>(null);
  const [modalDescricaoAberto, setModalDescricaoAberto] = useState(false);
  const [relatorioDescricao, setRelatorioDescricao] = useState<Relatorio | null>(null);
  const [modalAgendarAberto, setModalAgendarAberto] = useState(false);
  const [relatorioAgendar, setRelatorioAgendar] = useState<Relatorio | null>(null);
  const [exportandoPDFId, setExportandoPDFId] = useState<string | null>(null);
  const [exportandoCSVId, setExportandoCSVId] = useState<string | null>(null);
  const [exportandoExcelId, setExportandoExcelId] = useState<string | null>(null);
  const [visualizandoId, setVisualizandoId] = useState<string | null>(null);
  const [consultandoExcluidosId, setConsultandoExcluidosId] = useState<string | null>(null);
  const [agendamentoParaExcluir, setAgendamentoParaExcluir] = useState<string | null>(null);
  const [excluindoAgendamentoId, setExcluindoAgendamentoId] = useState<string | null>(null);
  const [dadosModal, setDadosModal] = useState<DadosRelatorioItem[]>([]);
  const [nomeRelatorioModal, setNomeRelatorioModal] = useState('');
  const [relatorioIdModal, setRelatorioIdModal] = useState<string | null>(null);
  const [modalVisualizarAberto, setModalVisualizarAberto] = useState(false);

  const gerarLinkTemporario = useGerarLinkTemporario();
  const gerarRelatorioPDF = useGerarRelatorioPDF();
  const gerarRelatorioCSV = useGerarRelatorioCSV();
  const { aguardarConclusao } = useAguardarSolicitacao();
  const deletarAgendamento = useDeletarAgendamento();

  const relatoriosOrdenados = useMemo(() => {
    return [...relatorios].sort((relatorioA, relatorioB) => {
      const comparacao = relatorioA.numero.localeCompare(relatorioB.numero, 'pt-BR', {
        numeric: true,
        sensitivity: 'base',
      });

      return ordemNumeroRelatorio === 'asc' ? comparacao : comparacao * -1;
    });
  }, [ordemNumeroRelatorio, relatorios]);

  const inicio = (paginaAtual - 1) * itensPorPagina;
  const fim = inicio + itensPorPagina;
  const relatoriosPaginados = relatoriosOrdenados.slice(inicio, fim);

  const cnpjsConsulta = useMemo(() => {
    if (!expandido) return [] as string[];
    return cnpjsSelecionados.length > 0 ? cnpjsSelecionados : [''];
  }, [cnpjsSelecionados, expandido]);

  const agendamentosQueries = useQueries({
    queries: cnpjsConsulta.map(cnpj => ({
      queryKey: ['relatorios', 'agendamentos-expandidos', expandido, cnpj],
      queryFn: () =>
        listarAgendamentosApi({
          relatorioId: Number(expandido),
          cnpjContribuinte: cnpj,
        }),
      enabled: Boolean(expandido),
      staleTime: 60 * 1000,
    })),
  });

  const agendamentosApi = useMemo(() => {
    const itens = agendamentosQueries.flatMap(query => query.data ?? []);
    const unicos = new Map<number, AgendamentoApi>();

    for (const item of itens) {
      unicos.set(item.id, item);
    }

    return Array.from(unicos.values());
  }, [agendamentosQueries]);

  const agendamentosPorId = useMemo(() => {
    return new Map(agendamentosApi.map(agendamento => [agendamento.id.toString(), agendamento]));
  }, [agendamentosApi]);

  const itensExpandidos = useMemo<ItemRelatorio[]>(() => {
    return agendamentosApi.map(agendamento => {
      const dataHora = new Date(agendamento.DataHora_Agendamento);

      return {
        id: agendamento.id.toString(),
        cnpj: agendamento.CNPJ_Contribuinte,
        status:
          agendamento.Agendamento_Status === 'Relatório Disponivel'
            ? 'Disponível'
            : 'Processado(Sem dados)',
        dataProcessamento: dataHora.toLocaleDateString('pt-BR'),
        horaProcessamento: dataHora.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
        }),
        idProcessamento: agendamento.id.toString(),
        PDFRelatorio: agendamento.Caminho_Relatorio || undefined,
      };
    });
  }, [agendamentosApi]);

  const isLoadingAgendamentos = agendamentosQueries.some(query => query.isLoading);

  const alternarOrdenacaoNumero = () => {
    setOrdemNumeroRelatorio(ordemAtual => (ordemAtual === 'asc' ? 'desc' : 'asc'));
  };

  const toggleExpand = (id: string) => setExpandido(expandido === id ? null : id);

  const mensagemSemAgendamentos = hasEmpresaSelecionada
    ? 'Este relatório não possui agendamentos para a empresa selecionada no header global.'
    : 'Este relatório não possui agendamentos disponíveis com os filtros atuais.';

  const handleConfigurar = (relatorio: Relatorio) => {
    setRelatorioSelecionado(relatorio.id);
    setRelatorioConfigurar(relatorio);
    setModalConfiguracaoAberto(true);
  };

  const handleAbrirDescricao = (relatorio: Relatorio) => {
    setRelatorioDescricao(relatorio);
    setModalDescricaoAberto(true);
  };

  const handleAbrirAgendar = (relatorio: Relatorio) => {
    setRelatorioAgendar(relatorio);
    setModalAgendarAberto(true);
  };

  const extrairBucketEKey = (caminho: string) => {
    if (!caminho) {
      throw new Error('Caminho de arquivo inválido.');
    }

    return {
      bucket: 'imposto-cache',
      key: caminho,
      nomeArquivo: caminho.split('/').pop() || caminho,
    };
  };

  const iniciarDownload = (url: string, nomeArquivo?: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noreferrer';

    if (nomeArquivo) {
      link.download = nomeArquivo;
    }

    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleGerarPDF = async (agendamentoId: string, _caminhoPdf?: string) => {
    setExportandoPDFId(agendamentoId);

    try {
      const solicitacaoPdf = await gerarRelatorioPDF.mutateAsync({
        agendamento_id: Number(agendamentoId),
      });

      if (!solicitacaoPdf?.solicitacao_id) {
        throw new Error('Não foi possível gerar o PDF.');
      }

      const solicitacao = await aguardarConclusao(solicitacaoPdf.solicitacao_id);
      const caminhoCompleto = solicitacao.Caminho_Relatorio;

      if (!caminhoCompleto) {
        throw new Error('Caminho do arquivo não retornado pela API.');
      }

      const { bucket, key, nomeArquivo } = extrairBucketEKey(caminhoCompleto);
      const linkResponse = await gerarLinkTemporario.mutateAsync({
        Funcao: 'GET',
        Bucket: bucket,
        Key: key,
      });

      if (!linkResponse.status?.resultado) {
        throw new Error(linkResponse.status?.mensagem || 'Não foi possível gerar o link do PDF.');
      }

      const urlDownload =
        typeof linkResponse.data === 'string' ? linkResponse.data : linkResponse.data?.url;

      if (!urlDownload) {
        throw new Error('Link de download não retornado pela API.');
      }

      iniciarDownload(urlDownload, nomeArquivo);
      toast({
        title: 'PDF gerado com sucesso',
        description: 'O download do relatório foi iniciado.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao exportar PDF',
        description: error instanceof Error ? error.message : 'Não foi possível exportar o PDF.',
      });
    } finally {
      setExportandoPDFId(null);
    }
  };

  const handleGerarCSV = async (agendamentoId: string) => {
    setExportandoCSVId(agendamentoId);

    try {
      const solicitacaoCsv = await gerarRelatorioCSV.mutateAsync({
        agendamento_id: Number(agendamentoId),
      });

      if (!solicitacaoCsv?.solicitacao_id) {
        throw new Error('Não foi possível gerar o CSV.');
      }

      const solicitacao = await aguardarConclusao(solicitacaoCsv.solicitacao_id);
      const caminhoCsv = solicitacao.Caminho_Relatorio;

      if (!caminhoCsv) {
        throw new Error('Caminho do arquivo não retornado pela API.');
      }

      const { bucket, key, nomeArquivo } = extrairBucketEKey(caminhoCsv);
      const linkResponse = await gerarLinkTemporario.mutateAsync({
        Funcao: 'GET',
        Bucket: bucket,
        Key: key,
      });

      if (!linkResponse.status?.resultado) {
        throw new Error(linkResponse.status?.mensagem || 'Não foi possível gerar o link do CSV.');
      }

      const urlDownload =
        typeof linkResponse.data === 'string' ? linkResponse.data : linkResponse.data?.url;

      if (!urlDownload) {
        throw new Error('Link de download não retornado pela API.');
      }

      iniciarDownload(urlDownload, nomeArquivo);
      toast({
        title: 'CSV gerado com sucesso',
        description: 'O download do relatório foi iniciado.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao exportar CSV',
        description: error instanceof Error ? error.message : 'Não foi possível exportar o CSV.',
      });
    } finally {
      setExportandoCSVId(null);
    }
  };

  const handleExportarExcel = async (agendamentoId: string, relatorio: Relatorio) => {
    setExportandoExcelId(agendamentoId);

    try {
      const [dados, campos] = await Promise.all([
        listarDadosApi({ agendamentoId: Number(agendamentoId) }),
        listarCamposApi(relatorio.id).catch(() => null),
      ]);

      if (dados.length === 0) {
        throw new Error('Não há dados disponíveis para exportar.');
      }

      const colunasMonetarias = new Set(
        (campos ?? []).filter(campo => campo.Totalizador === 'S').map(campo => campo.Nome_Campo)
      );

      exportToExcel(dados, formatExcelFileName(relatorio.nome), colunasMonetarias);

      toast({
        title: 'Exportação concluída',
        description: 'O arquivo Excel foi baixado com sucesso.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao exportar',
        description:
          error instanceof Error ? error.message : 'Ocorreu um erro ao exportar os dados.',
      });
    } finally {
      setExportandoExcelId(null);
    }
  };

  const handleVisualizarDados = async (agendamentoId: string, relatorio: Relatorio) => {
    setVisualizandoId(agendamentoId);

    try {
      const dados = await listarDadosApi({ agendamentoId: Number(agendamentoId) });

      if (dados.length === 0) {
        throw new Error('Não há dados disponíveis para visualizar.');
      }

      setDadosModal(dados.map(item => ({ ...item })));
      setNomeRelatorioModal(relatorio.nome);
      setRelatorioIdModal(relatorio.id);
      setModalVisualizarAberto(true);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar',
        description:
          error instanceof Error ? error.message : 'Ocorreu um erro ao carregar os dados.',
      });
    } finally {
      setVisualizandoId(null);
    }
  };

  const handleVisualizarItensExcluidos = async (agendamentoId: string, relatorio: Relatorio) => {
    setConsultandoExcluidosId(agendamentoId);

    try {
      const itensExcluidos = await listarItensExcluidosApi({ agendamentoId });

      if (itensExcluidos.length === 0) {
        throw new Error('Não foram identificados itens excluídos neste agendamento.');
      }

      setDadosModal(itensExcluidos.map(item => ({ ...item })));
      setNomeRelatorioModal(`Itens Excluídos - ${relatorio.nome}`);
      setModalVisualizarAberto(true);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Itens excluídos',
        description:
          error instanceof Error ? error.message : 'Não foi possível consultar os itens excluídos.',
      });
    } finally {
      setConsultandoExcluidosId(null);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!agendamentoParaExcluir) return;

    setExcluindoAgendamentoId(agendamentoParaExcluir);

    try {
      await deletarAgendamento.mutateAsync({
        agendamento_id: agendamentoParaExcluir,
      });

      const novaSelecao = new Set(agendamentosSelecionados);
      novaSelecao.delete(agendamentoParaExcluir);
      setAgendamentosSelecionados(novaSelecao);

      await queryClient.invalidateQueries({ queryKey: ['relatorios'] });

      toast({
        title: 'Agendamento deletado',
        description: 'O agendamento foi removido com sucesso.',
      });
      setAgendamentoParaExcluir(null);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao deletar agendamento',
        description:
          error instanceof Error ? error.message : 'Não foi possível deletar o agendamento.',
      });
    } finally {
      setExcluindoAgendamentoId(null);
    }
  };

  return (
    <>
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-4 font-medium text-sm w-16">
                    <button
                      type="button"
                      onClick={alternarOrdenacaoNumero}
                      className="inline-flex items-center gap-1 text-sm font-medium text-foreground transition-colors hover:text-primary"
                      title={
                        ordemNumeroRelatorio === 'asc'
                          ? 'Ordenado em ordem crescente. Clique para ordenar em ordem decrescente.'
                          : 'Ordenado em ordem decrescente. Clique para ordenar em ordem crescente.'
                      }
                      aria-label={
                        ordemNumeroRelatorio === 'asc'
                          ? 'Ordenado em ordem crescente. Clique para ordenar em ordem decrescente.'
                          : 'Ordenado em ordem decrescente. Clique para ordenar em ordem crescente.'
                      }
                    >
                      <span>Nº</span>
                      {ordemNumeroRelatorio === 'asc' ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </th>
                  <th className="text-left p-4 font-medium text-sm">Nome do Relatório</th>
                  <th className="text-left p-4 font-medium text-sm w-32">Tipo</th>
                  <th className="text-left p-4 font-medium text-sm w-40">Entrada/Saída</th>
                  <th className="text-center p-4 font-medium text-sm w-44">Ações</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={5} className="p-12">
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <div className="text-center">
                          <h3 className="text-lg font-semibold text-foreground">
                            Carregando relatórios
                          </h3>
                          <p className="text-muted-foreground mt-1">
                            Buscando os relatórios disponíveis...
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {!isLoading && relatoriosPaginados.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-12 text-center">
                      <div className="space-y-4">
                        <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                          <FileText className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-foreground">
                            Nenhum relatório encontrado
                          </h3>
                          <p className="text-muted-foreground mt-2">
                            Não foram encontrados relatórios com os filtros aplicados.
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  relatoriosPaginados.map((relatorio, index) => (
                    <Fragment key={relatorio.id}>
                      <motion.tr
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-b border-border hover:bg-muted/50 transition-colors"
                      >
                        <td className="p-4 font-medium">{relatorio.numero}</td>
                        <td className="p-4">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="truncate cursor-default">{relatorio.nome}</div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-md">
                                <p>{relatorio.nome}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline">{relatorio.tipo}</Badge>
                        </td>
                        <td className="p-4">{relatorio.entradaSaida}</td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            <ActionTooltipButton label="Expandir">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => toggleExpand(relatorio.id)}
                              >
                                <ChevronDown
                                  className={cn(
                                    'h-4 w-4 transition-transform duration-200',
                                    expandido === relatorio.id && 'rotate-180'
                                  )}
                                />
                              </Button>
                            </ActionTooltipButton>

                            <ActionTooltipButton label="Agendamentos">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleAbrirAgendar(relatorio)}
                              >
                                <Calendar className="h-4 w-4" />
                              </Button>
                            </ActionTooltipButton>

                            <ActionTooltipButton label="Configuração">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleConfigurar(relatorio)}
                              >
                                <Settings className="h-4 w-4" />
                              </Button>
                            </ActionTooltipButton>

                            <ActionTooltipButton label="Regras">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleAbrirDescricao(relatorio)}
                              >
                                <Info className="h-4 w-4" />
                              </Button>
                            </ActionTooltipButton>
                          </div>
                        </td>
                      </motion.tr>

                      {expandido === relatorio.id && (
                        <motion.tr
                          key={`${relatorio.id}-expanded`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <td colSpan={5} className="p-0">
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              {isLoadingAgendamentos ? (
                                <div className="bg-muted/30 py-8">
                                  <div className="flex flex-col items-center justify-center space-y-3">
                                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                    <p className="text-sm text-muted-foreground">
                                      Carregando agendamentos...
                                    </p>
                                  </div>
                                </div>
                              ) : itensExpandidos.length === 0 ? (
                                <div className="bg-muted/30 py-8">
                                  <div className="flex flex-col items-center justify-center space-y-3">
                                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                                      <Calendar className="h-6 w-6 text-muted-foreground" />
                                    </div>
                                    <div className="text-center">
                                      <p className="text-sm font-medium text-foreground">
                                        Nenhum agendamento encontrado
                                      </p>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {mensagemSemAgendamentos}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-muted/30">
                                  {itensExpandidos.map((item, itemIndex) => (
                                    <motion.div
                                      key={item.id}
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: itemIndex * 0.05 }}
                                      className="flex items-center px-4 py-3 border-t border-border/50 hover:bg-muted/50 transition-colors"
                                    >
                                      <Checkbox
                                        checked={agendamentosSelecionados.has(item.id)}
                                        onCheckedChange={checked => {
                                          const newSelected = new Set(agendamentosSelecionados);
                                          if (checked) {
                                            newSelected.add(item.id);
                                          } else {
                                            newSelected.delete(item.id);
                                          }
                                          setAgendamentosSelecionados(newSelected);
                                        }}
                                        className="mr-3"
                                      />
                                      <div className="flex-1 flex items-center gap-4 text-sm">
                                        <span className="font-mono text-muted-foreground">
                                          {formatCNPJ(item.cnpj)}
                                        </span>
                                        <Badge
                                          variant={
                                            item.status === 'Disponível' ? 'default' : 'secondary'
                                          }
                                          className="whitespace-nowrap"
                                        >
                                          {item.status === 'Disponível' && (
                                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                                          )}
                                          {item.status === 'Processado(Sem dados)' && (
                                            <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2" />
                                          )}
                                          {item.status}
                                        </Badge>
                                        <span className="text-muted-foreground">
                                          Data: {item.dataProcessamento} {item.horaProcessamento}
                                        </span>
                                        <span className="text-muted-foreground">
                                          ID: {item.idProcessamento}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() =>
                                                  handleGerarPDF(
                                                    item.idProcessamento,
                                                    item.PDFRelatorio
                                                  )
                                                }
                                                disabled={exportandoPDFId === item.idProcessamento}
                                              >
                                                {exportandoPDFId === item.idProcessamento ? (
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                  <FileText className="h-4 w-4" />
                                                )}
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Exportar PDF</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>

                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => handleGerarCSV(item.idProcessamento)}
                                                disabled={
                                                  !agendamentosPorId.get(item.idProcessamento)
                                                    ?.Caminho_Relatorio_CSV ||
                                                  exportandoCSVId === item.idProcessamento
                                                }
                                              >
                                                {exportandoCSVId === item.idProcessamento ? (
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                  <Download className="h-4 w-4" />
                                                )}
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Exportar CSV</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>

                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() =>
                                                  handleExportarExcel(
                                                    item.idProcessamento,
                                                    relatorio
                                                  )
                                                }
                                                disabled={
                                                  exportandoExcelId === item.idProcessamento
                                                }
                                              >
                                                {exportandoExcelId === item.idProcessamento ? (
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                  <FileSpreadsheet className="h-4 w-4" />
                                                )}
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Exportar Excel</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>

                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() =>
                                                  handleVisualizarItensExcluidos(
                                                    item.idProcessamento,
                                                    relatorio
                                                  )
                                                }
                                                disabled={
                                                  consultandoExcluidosId === item.idProcessamento
                                                }
                                              >
                                                {consultandoExcluidosId === item.idProcessamento ? (
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                  <AlertCircle className="h-4 w-4" />
                                                )}
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Itens Excluídos</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>

                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() =>
                                                  handleVisualizarDados(
                                                    item.idProcessamento,
                                                    relatorio
                                                  )
                                                }
                                                disabled={visualizandoId === item.idProcessamento}
                                              >
                                                {visualizandoId === item.idProcessamento ? (
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                  <Eye className="h-4 w-4" />
                                                )}
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Visualizar relatório parametrizado</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>

                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-destructive hover:text-destructive"
                                                onClick={() =>
                                                  setAgendamentoParaExcluir(item.idProcessamento)
                                                }
                                                disabled={
                                                  excluindoAgendamentoId === item.idProcessamento
                                                }
                                              >
                                                {excluindoAgendamentoId === item.idProcessamento ? (
                                                  <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                  <Trash2 className="h-4 w-4" />
                                                )}
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Deletar Agendamento</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      </div>
                                    </motion.div>
                                  ))}
                                </div>
                              )}
                            </motion.div>
                          </td>
                        </motion.tr>
                      )}
                    </Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="md:hidden p-4 space-y-4">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="text-center">
                <h3 className="text-lg font-semibold text-foreground">Carregando relatórios</h3>
                <p className="text-muted-foreground mt-1">Buscando os relatórios disponíveis...</p>
              </div>
            </div>
          )}
          {!isLoading && relatoriosPaginados.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-semibold text-foreground">
                  Nenhum relatório encontrado
                </h3>
                <p className="text-muted-foreground mt-2">
                  Não foram encontrados relatórios com os filtros aplicados.
                </p>
              </div>
            </div>
          )}
          {!isLoading &&
            relatoriosPaginados
              .filter(relatorio => !expandido || expandido === relatorio.id)
              .map((relatorio, index) => (
                <motion.div
                  key={relatorio.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-background border border-border rounded-lg overflow-hidden"
                >
                  <div className="p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-xs text-muted-foreground">Nº {relatorio.numero}</span>
                        <h4 className="font-medium text-sm mt-1">{relatorio.nome}</h4>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {relatorio.tipo}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {relatorio.entradaSaida}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleExpand(relatorio.id)}
                        className="flex-1"
                      >
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 mr-2 transition-transform duration-200',
                            expandido === relatorio.id && 'rotate-180'
                          )}
                        />
                        {expandido === relatorio.id ? 'Recolher' : 'Expandir'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAbrirAgendar(relatorio)}
                      >
                        <Calendar className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAbrirDescricao(relatorio)}
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandido === relatorio.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden border-t border-border"
                      >
                        {isLoadingAgendamentos ? (
                          <div className="bg-muted/30 py-6 flex flex-col items-center justify-center space-y-2">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                            <p className="text-sm text-muted-foreground">
                              Carregando agendamentos...
                            </p>
                          </div>
                        ) : itensExpandidos.length === 0 ? (
                          <div className="bg-muted/30 py-6 flex flex-col items-center justify-center space-y-2">
                            <Calendar className="h-6 w-6 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                              Nenhum agendamento encontrado
                            </p>
                            <p className="max-w-xs text-center text-xs text-muted-foreground">
                              {mensagemSemAgendamentos}
                            </p>
                          </div>
                        ) : (
                          <div className="bg-muted/30 divide-y divide-border/50">
                            {itensExpandidos.map(item => (
                              <div key={item.id} className="p-3 space-y-2">
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={agendamentosSelecionados.has(item.id)}
                                    onCheckedChange={checked => {
                                      const newSelected = new Set(agendamentosSelecionados);
                                      if (checked) newSelected.add(item.id);
                                      else newSelected.delete(item.id);
                                      setAgendamentosSelecionados(newSelected);
                                    }}
                                  />
                                  <Badge
                                    variant={item.status === 'Disponível' ? 'default' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {item.status}
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground space-y-1">
                                  <p>CNPJ: {formatCNPJ(item.cnpj)}</p>
                                  <p>
                                    Data: {item.dataProcessamento} {item.horaProcessamento} · ID:{' '}
                                    {item.idProcessamento}
                                  </p>
                                </div>
                                <div className="grid grid-cols-2 gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() =>
                                      handleGerarPDF(item.idProcessamento, item.PDFRelatorio)
                                    }
                                    disabled={exportandoPDFId === item.idProcessamento}
                                  >
                                    {exportandoPDFId === item.idProcessamento ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <FileText className="h-3 w-3 mr-1" />
                                    )}
                                    PDF
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => handleGerarCSV(item.idProcessamento)}
                                    disabled={
                                      !agendamentosPorId.get(item.idProcessamento)
                                        ?.Caminho_Relatorio_CSV ||
                                      exportandoCSVId === item.idProcessamento
                                    }
                                  >
                                    {exportandoCSVId === item.idProcessamento ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <Download className="h-3 w-3 mr-1" />
                                    )}
                                    CSV
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() =>
                                      handleExportarExcel(item.idProcessamento, relatorio)
                                    }
                                    disabled={exportandoExcelId === item.idProcessamento}
                                  >
                                    {exportandoExcelId === item.idProcessamento ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <FileSpreadsheet className="h-3 w-3 mr-1" />
                                    )}
                                    Excel
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() =>
                                      handleVisualizarItensExcluidos(
                                        item.idProcessamento,
                                        relatorio
                                      )
                                    }
                                    disabled={consultandoExcluidosId === item.idProcessamento}
                                  >
                                    {consultandoExcluidosId === item.idProcessamento ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <AlertCircle className="h-3 w-3 mr-1" />
                                    )}
                                    Excluídos
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() =>
                                      handleVisualizarDados(item.idProcessamento, relatorio)
                                    }
                                    disabled={visualizandoId === item.idProcessamento}
                                  >
                                    {visualizandoId === item.idProcessamento ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <Eye className="h-3 w-3 mr-1" />
                                    )}
                                    Dados
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs text-destructive"
                                    onClick={() => setAgendamentoParaExcluir(item.idProcessamento)}
                                    disabled={excluindoAgendamentoId === item.idProcessamento}
                                  >
                                    {excluindoAgendamentoId === item.idProcessamento ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-3 w-3 mr-1" />
                                    )}
                                    Excluir
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
        </div>
      </div>

      <ModalConfiguracao
        aberto={modalConfiguracaoAberto}
        onFechar={() => {
          setModalConfiguracaoAberto(false);
          setRelatorioSelecionado(null);
          setRelatorioConfigurar(null);
        }}
        relatorioId={relatorioSelecionado}
        relatorioNome={relatorioConfigurar?.nome}
      />

      <ModalDescricaoRegra
        aberto={modalDescricaoAberto}
        onFechar={() => {
          setModalDescricaoAberto(false);
          setRelatorioDescricao(null);
        }}
        titulo={relatorioDescricao?.nome ?? ''}
        descricao={relatorioDescricao?.descricao ?? ''}
        relatorioId={Number(relatorioDescricao?.id ?? 0)}
      />

      <ModalAgendarProcessamento
        aberto={modalAgendarAberto}
        onFechar={() => {
          setModalAgendarAberto(false);
          setRelatorioAgendar(null);
        }}
        nomeRelatorio={relatorioAgendar?.nome ?? ''}
        relatorioId={Number(relatorioAgendar?.id ?? null)}
      />

      <ModalVisualizarDados
        open={modalVisualizarAberto}
        onOpenChange={setModalVisualizarAberto}
        dados={dadosModal}
        nomeRelatorio={nomeRelatorioModal}
        relatorioId={relatorioIdModal}
      />

      <AlertDialog
        open={Boolean(agendamentoParaExcluir)}
        onOpenChange={open => {
          if (!open && !deletarAgendamento.isPending) {
            setAgendamentoParaExcluir(null);
            setExcluindoAgendamentoId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deletar agendamento</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove o agendamento selecionado. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deletarAgendamento.isPending}
              onClick={() => {
                setAgendamentoParaExcluir(null);
                setExcluindoAgendamentoId(null);
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmarExclusao}
              disabled={deletarAgendamento.isPending}
            >
              {deletarAgendamento.isPending ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
